import { Component, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { TeamsService, TeamMember, TeamSummary } from '../../../../../../services/entity-services/teams.service';

interface QuotaActionTile {
  value: string;
  label: string;
  description: string;
  icon: string;
}

/** The name of the system-seeded participant team to exclude from selectors. */
const PARTICIPANT_TEAM_NAME = 'Participant';

@Component({
  selector: 'app-step-ua-quota-limit',
  templateUrl: './step-ua-quota-limit.component.html',
  styleUrls: ['./step-ua-quota-limit.component.css']
})
export class StepUaQuotaLimitComponent implements OnInit {
  @Input() form!: FormGroup;

  /** All teams fetched from the API, minus the Participant team. */
  selectableTeams: TeamSummary[] = [];
  teamMembers = new Map<string, TeamMember[]>();

  /** Index of the currently expanded accordion panel; null means all collapsed. */
  expandedBandIndex: number | null = null;

  readonly quotaActionTiles: QuotaActionTile[] = [
    {
      value: 'DeactivateSubscription',
      label: 'Deactivate Subscription',
      description: 'Suspend the Azure subscription when spending hits 100% of the budget.',
      icon: 'block'
    },
    {
      value: 'DeleteResourcesAndSetReadOnly',
      label: 'Delete & Set Read-Only',
      description: 'Remove all resources and lock the subscription to read-only.',
      icon: 'lock'
    },
    {
      value: 'ExtendQuota',
      label: 'Extend Budget',
      description: 'Automatically grant additional budget when the limit is reached.',
      icon: 'add_card'
    }
  ];

  constructor(
    private readonly fb: FormBuilder,
    private readonly teamsService: TeamsService,
  ) {}

  ngOnInit(): void {
    this.teamsService.getAll().subscribe(teams => {
      // Exclude the Participant team — participants are auto-notified by the system.
      // TODO: Replace with teamType filter once teamType is added to the backend Team model.
      this.selectableTeams = teams.filter(t => t.name !== PARTICIPANT_TEAM_NAME);
    });
  }

  get isQuotaEnabled(): boolean {
    return this.selectedAction !== 'DoNothing';
  }

  get selectedAction(): string {
    return this.form?.get('actionType')?.value ?? 'DoNothing';
  }

  selectAction(value: string): void {
    this.form.get('actionType')?.setValue(value);
    if (value === 'DoNothing') {
      this.form.get('thresholdAmount')?.setValue(null);
      this.form.get('additionalAmount')?.setValue(null);
      this.bandsArray.clear();
      this.expandedBandIndex = null;
    } else if (this.bandsArray.length === 0) {
      // Auto-add the mandatory 100% band when quota is first enabled.
      this.bandsArray.push(this.createBandGroup(100));
      this.expandedBandIndex = 0;
    }
    if (value !== 'ExtendQuota') {
      this.form.get('additionalAmount')?.setValue(null);
    }
    this.form.updateValueAndValidity();
  }

  // ── Bands ──────────────────────────────────────────────────────────────────

  get bandsArray(): FormArray {
    return this.form.get('notificationBands') as FormArray;
  }

  getBandGroup(bandIndex: number): FormGroup {
    return this.bandsArray.at(bandIndex) as FormGroup;
  }

  getBandNotificationsArray(bandIndex: number): FormArray {
    return this.getBandGroup(bandIndex).get('notifications') as FormArray;
  }

  /** The 100% band is always the last band (fixed). */
  isFixedBand(bandIndex: number): boolean {
    return this.getBandGroup(bandIndex).get('triggerPercentage')?.value === 100;
  }

  addCustomBand(): void {
    // Insert a new band before the 100% band and expand it.
    const insertAt = this.bandsArray.length - 1;
    this.bandsArray.insert(insertAt, this.createBandGroup(null));
    this.expandedBandIndex = insertAt;
  }

  onBandExpanded(bandIndex: number): void {
    this.expandedBandIndex = bandIndex;
  }

  onBandClosed(bandIndex: number): void {
    if (this.expandedBandIndex === bandIndex) {
      this.expandedBandIndex = null;
    }
  }

  /** Returns a comma-separated list of team names configured in the band, for the collapsed summary. */
  getBandTeamNames(bandIndex: number): string {
    const names = this.getBandNotificationsArray(bandIndex).controls
      .map(ctrl => (ctrl as FormGroup).get('teamName')?.value ?? '')
      .filter(Boolean);
    return names.length ? names.join(', ') : 'No teams';
  }

  removeBand(bandIndex: number): void {
    this.bandsArray.removeAt(bandIndex);
  }

  private createBandGroup(triggerPercentage: number | null): FormGroup {
    return this.fb.group({
      triggerPercentage: [triggerPercentage],
      notifications: this.fb.array([])
    });
  }

  // ── Per-band notifications ─────────────────────────────────────────────────

  addNotificationToBand(bandIndex: number): void {
    const notifs = this.getBandNotificationsArray(bandIndex);
    notifs.push(this.fb.group({
      teamId: [''],
      teamName: [''],
      userIds: [[]],
      emailChannel: [true],
      appChannel: [false]
    }));
  }

  removeNotificationFromBand(bandIndex: number, notifIndex: number): void {
    this.getBandNotificationsArray(bandIndex).removeAt(notifIndex);
  }

  getNotificationRow(bandIndex: number, notifIndex: number): FormGroup {
    return this.getBandNotificationsArray(bandIndex).at(notifIndex) as FormGroup;
  }

  getMembersForRow(bandIndex: number, notifIndex: number): TeamMember[] {
    const teamId = this.getNotificationRow(bandIndex, notifIndex).get('teamId')?.value;
    return teamId ? (this.teamMembers.get(teamId) ?? []) : [];
  }

  onTeamSelected(teamId: string, bandIndex: number, notifIndex: number): void {
    if (!teamId) return;
    if (!this.teamMembers.has(teamId)) {
      this.teamsService.getMembersByTeam(teamId).subscribe(members => {
        this.teamMembers.set(teamId, members);
      });
    }
    const team = this.selectableTeams.find(t => t.id === teamId);
    const row = this.getNotificationRow(bandIndex, notifIndex);
    row.get('teamName')?.setValue(team?.name ?? '');
    row.get('userIds')?.setValue([]);
  }

  toggleUserId(userId: string, bandIndex: number, notifIndex: number): void {
    const row = this.getNotificationRow(bandIndex, notifIndex);
    const current: string[] = row.get('userIds')?.value ?? [];
    const idx = current.indexOf(userId);
    const updated = idx > -1
      ? current.filter(id => id !== userId)
      : [...current, userId];
    row.get('userIds')?.setValue(updated);
  }

  isUserSelected(userId: string, bandIndex: number, notifIndex: number): boolean {
    return (this.getNotificationRow(bandIndex, notifIndex).get('userIds')?.value ?? []).includes(userId);
  }
}
